import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import axios from 'axios';
import SuperAdminLayout from '../../../layouts/SuperAdminLayout';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '../../../utils/api';

const cellStyle = {
  textAlign: 'center',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  padding: '6px',
};

const headerStyle = {
  textAlign: 'center',
  fontWeight: 'bold',
  fontSize: '13px',
  color: '#fff',
  padding: '6px',
  whiteSpace: 'nowrap',
};

const ClosingStockReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const superAdminId = JSON.parse(localStorage.getItem('user'))?.id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/api/reports/closing-stock/${superAdminId}`);
        setData(res.data);
      } catch (err) {
        console.error('Error fetching report:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [superAdminId]);

  const downloadExcel = () => {
    const rows = data.map((item) => ({
      'Product Name': item.productName,
      'Opening Qty': item.openingQty,
      'Opening Rate': item.openingRate,
      'Opening Total': item.openingQty * item.openingRate,

      'Inward Qty': item.inwardQty,
      'Inward Rate': item.inwardRate,
      'Inward Total': item.inwardQty * item.inwardRate,

      'Sale Qty': item.outwardQty,
      'Sale Rate': item.outwardRate,
      'Sale Total': item.outwardQty * item.outwardRate,

      'Closing Qty': item.closingQty,
      'Avg Price': item.closingRate,
      'Closing Total': item.closingQty * item.closingRate,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Report');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'closing-stock-report.xlsx');
  };

  return (
    <SuperAdminLayout>
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Closing Stock Report
      </Typography>

      <Button variant="outlined" sx={{ mb: 2 }} onClick={downloadExcel}>
        Download Excel
      </Button>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            mt: 2,
            borderRadius: 2,
            maxHeight: '85vh',
            overflowX: 'auto',
          }}
        >
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ background: '#f4a261', ...headerStyle }}
                  rowSpan={2}
                >
                  PRODUCT
                </TableCell>

                <TableCell
                  colSpan={3}
                  sx={{ background: '#63e686', ...headerStyle }}
                >
                  OPENING STOCK
                </TableCell>
                <TableCell
                  colSpan={3}
                  sx={{ background: '#2a9d8f', ...headerStyle }}
                >
                  INWARD STOCK
                </TableCell>
                <TableCell
                  colSpan={3}
                  sx={{ background: '#e63946', ...headerStyle }}
                >
                  OUTWARD STOCK
                </TableCell>
                <TableCell
                  colSpan={3}
                  sx={{ background: '#9294fc', ...headerStyle }}
                >
                  CLOSING STOCK
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ background: '#d3f9d8', ...cellStyle }}>
                  QTY
                </TableCell>
                <TableCell sx={{ background: '#d3f9d8', ...cellStyle }}>
                  RATE
                </TableCell>
                <TableCell sx={{ background: '#d3f9d8', ...cellStyle }}>
                  TOTAL
                </TableCell>

                <TableCell sx={{ background: '#b2f7ef', ...cellStyle }}>
                  QTY
                </TableCell>
                <TableCell sx={{ background: '#b2f7ef', ...cellStyle }}>
                  RATE
                </TableCell>
                <TableCell sx={{ background: '#b2f7ef', ...cellStyle }}>
                  TOTAL
                </TableCell>

                <TableCell sx={{ background: '#fcd5ce', ...cellStyle }}>
                  QTY
                </TableCell>
                <TableCell sx={{ background: '#fcd5ce', ...cellStyle }}>
                  RATE
                </TableCell>
                <TableCell sx={{ background: '#fcd5ce', ...cellStyle }}>
                  TOTAL
                </TableCell>

                <TableCell sx={{ background: '#cccdfc', ...cellStyle }}>
                  QTY
                </TableCell>
                <TableCell sx={{ background: '#cccdfc', ...cellStyle }}>
                  RATE
                </TableCell>
                <TableCell sx={{ background: '#cccdfc', ...cellStyle }}>
                  TOTAL
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={cellStyle}>{item.productName}</TableCell>

                  <TableCell sx={cellStyle}>{item.openingQty}</TableCell>
                  <TableCell sx={cellStyle}>₹{item.openingRate}</TableCell>
                  <TableCell sx={cellStyle}>
                    ₹{item.openingTotal.toFixed(2)}
                  </TableCell>

                  <TableCell sx={cellStyle}>{item.inwardQty}</TableCell>
                  <TableCell sx={cellStyle}>₹{item.inwardRate}</TableCell>
                  <TableCell sx={cellStyle}>
                    ₹{item.inwardTotal.toFixed(2)}
                  </TableCell>

                  <TableCell sx={cellStyle}>{item.outwardQty}</TableCell>
                  <TableCell sx={cellStyle}>
                    ₹{item.outwardRate.toFixed(2)}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    ₹{item.outwardTotal.toFixed(2)}
                  </TableCell>

                  <TableCell sx={cellStyle}>{item.closingQty}</TableCell>
                  <TableCell sx={cellStyle}>₹{item.closingRate}</TableCell>
                  <TableCell sx={cellStyle}>
                    ₹{item.closingTotal.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SuperAdminLayout>
  );
};

export default ClosingStockReport;
